import { api } from '@/lib/api';
import type {
  Role,
  RolesFilters,
  RolesResponse,
  V3RolesResponse,
  CreateRoleInput,
  UpdateRoleInput,
  SchemaModel,
  SchemaField,
  RoleChangelogResponse,
} from './types';

// =============================================================================
// Roles API Functions
// Data source: MongoDB via GET /v4/legacy/roles
// =============================================================================

/**
 * Get roles with optional filtering, sorting, and pagination
 */
export async function getRoles(filters: RolesFilters = {}): Promise<RolesResponse> {
  const queryParams: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'roleId',
    sortDirection: filters.sortDirection ?? 1,
    populate: JSON.stringify(['created_by']),
    deleted: 'false',
  };

  // Add search filter (roleId regex search)
  if (filters.search) {
    queryParams.roleId = filters.search;
  }

  const response = await api.get<V3RolesResponse>('/v4/legacy/roles/', queryParams);

  // V3 API doesn't always return total count - estimate based on results
  const pageLength = filters.pageLength ?? 25;
  const pageNumber = response.pageNumber ?? 0;
  const roles = response.roles || [];
  const hasMore = roles.length === pageLength;
  const estimatedTotal = hasMore
    ? (pageNumber + 2) * pageLength
    : pageNumber * pageLength + roles.length;

  return {
    roles,
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? pageLength,
    total: estimatedTotal,
  };
}

/**
 * Get accurate total count of roles matching filters
 */
export async function getRolesCount(filters: RolesFilters = {}): Promise<number> {
  const queryParams: Record<string, string | number | boolean> = {
    count: true,
    deleted: 'false',
  };

  if (filters.search) {
    queryParams.roleId = filters.search;
  }

  const response = await api.get<{ count: number }>('/v4/legacy/roles/', queryParams);
  return response.count ?? 0;
}

/**
 * Get a single role by ID
 */
export async function getRole(roleId: string): Promise<Role> {
  const response = await api.get<{ role: Role }>(`/v4/legacy/roles/${roleId}`, {
    populate: JSON.stringify(['created_by']),
  });
  return response.role;
}

/**
 * Create a new role
 */
export async function createRole(data: CreateRoleInput): Promise<Role> {
  const response = await api.post<{ role: Role }>('/v4/legacy/roles/', data);
  return response.role;
}

/**
 * Update a role
 */
export async function updateRole(roleId: string, data: UpdateRoleInput): Promise<Role> {
  const response = await api.put<{ role: Role }>(`/v4/legacy/roles/${roleId}`, data);
  return response.role;
}

/**
 * Delete a role (soft delete)
 */
export async function deleteRole(roleId: string): Promise<void> {
  await api.delete(`/v4/legacy/roles/${roleId}`);
}

/**
 * Check if a roleId is unique
 * Returns true if unique (not in use), false otherwise
 */
export async function checkRoleIdUnique(
  roleId: string,
  excludeId?: string
): Promise<boolean> {
  const response = await api.get<V3RolesResponse>('/v4/legacy/roles/', {
    roleId_eq: roleId,
    deleted: 'all',
    pageLength: 1,
  });

  if (!response.roles || response.roles.length === 0) {
    return true;
  }

  // If we're updating an existing role, exclude it from the check
  if (excludeId && response.roles[0]._id === excludeId) {
    return true;
  }

  return false;
}

// =============================================================================
// Schema API Functions
// For dynamic permission model/field selection
// =============================================================================

/**
 * Get all available schema models
 */
export async function getSchemaModels(): Promise<SchemaModel[]> {
  const response = await api.get<{ schemas: SchemaModel[] }>('/v4/legacy/schemas/');
  return response.schemas || [];
}

/**
 * Get fields for a specific schema model
 */
export async function getSchemaFields(modelName: string): Promise<SchemaField[]> {
  const response = await api.get<{ fields: SchemaField[] }>(`/v4/legacy/schemas/${modelName}`);
  return response.fields || [];
}

// =============================================================================
// Changelog API Functions
// =============================================================================

/**
 * Get changelog for a specific role
 */
export async function getRoleChangelog(roleId: string): Promise<RoleChangelogResponse> {
  const response = await api.get<RoleChangelogResponse>(`/v4/legacy/roles/${roleId}/changelog`);
  return response;
}
